import {useAuth0} from '@auth0/auth0-react';
import {HttpClient} from '@sollapay/http-client';
import {useEffect, useMemo, useRef} from 'react';
import {useTranslation} from 'react-i18next';

import {useOptionalTheme} from '../../theme/components/ThemeProvider';
import {useLoginContextGetter} from '../components/Auth0Provider';
import {getCurrentPath} from '../utils/location.utils';
import {createRecoveringTokenGetter} from '../utils/token-recovery.utils';

export interface UseApiClientOptions {
  baseUrl: string;
}

export function useApiClient({baseUrl}: UseApiClientOptions) {
  const {getAccessTokenSilently, loginWithRedirect} = useAuth0();
  const {i18n} = useTranslation();
  const theme = useOptionalTheme()?.theme;
  const getLoginContext = useLoginContextGetter();
  const themeRef = useRef(theme);
  useEffect(() => {
    themeRef.current = theme;
  });

  const getToken = useMemo(
    () =>
      createRecoveringTokenGetter({
        getAccessTokenSilently,
        loginWithRedirect,
        getReturnToPath: getCurrentPath,
        getUiLocale: () => i18n.language,
        getTheme: () => themeRef.current,
        getLoginContext
      }),
    [getAccessTokenSilently, loginWithRedirect, i18n, getLoginContext]
  );

  return useMemo(() => new HttpClient({baseUrl, getToken}), [baseUrl, getToken]);
}
